import { useState } from "react";
import type { ReactNode } from "react";
import { Button } from "./Button";
import { TextField, CheckboxField } from "./form/Field";
import { FormError } from "./form/FormError";
import { subscribe } from "../api/writes";
import { messageForError, SUBSCRIPTION_MESSAGES } from "../lib/api-messages";
import { useSubmitAction } from "../hooks/useSubmitAction";
import * as check from "../lib/validate";
import styles from "./SubscribeForm.module.css";
import type { FieldErrors } from "../lib/form";
import type { SubscriberSource, SubscriptionRequest } from "../api/types";

/**
 * Optional buyer email capture (canon § 13).
 *
 * The consent sentence below is **core's wording**, published in canon
 * `contracts/API.md` § Public subscription shape for this form to display. Core stores it
 * verbatim next to the address under version `2026-08-v1`, so what it says here and what
 * the record says are the same sentence — which is the whole point of a consent record.
 * Change it here and the record stops matching what was shown; the wording moves in core
 * first, with a new version, or not at all.
 *
 * **The confirmation is deliberately identical for every outcome.** A new address, one
 * already subscribed and one that previously unsubscribed all produce the same `202` and
 * the same body, because any difference would turn this form into a way for anyone to ask
 * "is this person on Dealbid's list" about anyone. So the copy confirms what was received,
 * and never claims the address was added.
 */

/** Must match `SUBSCRIPTION_CONSENT_TEXT` in dealbid-core. */
export const CONSENT_TEXT =
  "I agree to receive Dealbid emails about new software deals. I can unsubscribe at any time.";

export function SubscribeForm({
  source,
  labelledExternally = false,
  inline = false,
  pitch,
}: {
  source: SubscriberSource;
  /**
   * The surface around the form already carries the heading and the promise. The footer's
   * subscribe card does (`WEB-007`, following the canvas), and printing "Subscribe" beside
   * "Get the board by email" made the card say the same thing twice in two voices.
   */
  labelledExternally?: boolean | undefined;
  /**
   * The design canvas's shape (`WEB-013`): the address beside its button on one row, with
   * no chrome of its own, because the card around it already is the chrome. Off, the form
   * is its own bordered panel and stacks — which is what `/submit` and any future surface
   * without a card want.
   *
   * The consent checkbox is present either way. The canvas draws no consent control, and
   * `contracts/API.md` § Public subscription shape is why this form has one regardless: it
   * displays the sentence core stores, and a record of a sentence nobody saw is not a
   * consent record.
   */
  inline?: boolean | undefined;
  /**
   * The card's own pitch — its heading and one muted line — rendered as the **first item of
   * the form's row** rather than beside the form (`WEB-035`).
   *
   * This is a layout fix with a measurement behind it. The canvas draws one row: the pitch,
   * then a 260px field, then its button. When the pitch sat outside the form, the form's
   * intrinsic width included the consent sentence on a single line — roughly 950px — so the
   * card wrapped, the pitch took a full row of its own, and the field stretched across 838px.
   * Nothing about that resembled the drawing.
   *
   * With the pitch inside the row, the consent's `flex-basis: 100%` wraps it under the whole
   * card, which is exactly where departure 7 always said it belonged: the drawing's row, plus
   * one line beneath it for the sentence canon requires be shown.
   */
  pitch?: ReactNode | undefined;
}) {
  const [email, setEmail] = useState("");
  const [consented, setConsented] = useState(false);

  const form = useSubmitAction<SubscriptionRequest, unknown>({
    prepare: () => {
      const errors: FieldErrors = {};
      const emailProblem = check.email(email, { required: true });
      if (emailProblem !== null) errors.email = emailProblem;
      if (!consented) {
        errors.acceptedConsent = "Tick this to confirm Dealbid may email you.";
      }
      if (Object.keys(errors).length > 0) return { ok: false, errors };

      return {
        ok: true,
        body: {
          email: email.trim().toLowerCase(),
          source,
          acceptedConsent: true,
        },
      };
    },
    send: (body, signal) => subscribe(body, signal),
  });

  if (form.status === "success") {
    return (
      <div
        className={`${styles.done} ${inline ? styles.inlineDone : ""}`.trim()}
        role="status"
      >
        <p className={styles.doneTitle}>Thanks — that’s recorded.</p>
        <p className={styles.doneBody}>
          Dealbid has your request for <span className="figure">{email.trim().toLowerCase()}</span>.
          Deal emails only, and every one of them carries an unsubscribe link.
        </p>
      </div>
    );
  }

  /*
   * The address, the consent and the button, in that order — and the order is the point.
   *
   * They are extracted so the inline variant can put them in a column of their own
   * (`WEB-037`) without the standalone variant gaining a wrapper it does not want: `.form`
   * is a grid whose gap separates exactly these three, and a `div` around them would collapse
   * that into one cell.
   *
   * Consent stays **before** the submit button in source order in both variants. The inline
   * layout draws the button up beside the field, as the canvas pairs them, so the visual
   * arrangement and the tab order differ by one step — deliberately in the safe direction: a
   * keyboard visitor reaches the sentence they are agreeing to before they reach the control
   * that agrees to it. The redrawn canvas settles where the control *sits*; it does not
   * settle what a keyboard reaches first, and this is unchanged by it.
   */
  const controls = (
    <>
      {/*
        The address. In the inline variant its label is hidden — the card's own "Subscribe"
        heading is the visible name — but the label still exists and is still bound, because
        the canvas's placeholder is not one and never announces a name.
      */}
      <div className={inline ? styles.entry : undefined}>
        <TextField
          name="email"
          label="Email address"
          type="email"
          inputMode="email"
          autoComplete="email"
          maxLength={254}
          required
          value={email}
          onChange={setEmail}
          labelHidden={inline}
          {...(inline ? { placeholder: "you@company.com" } : {})}
          {...(form.fieldErrors.email === undefined ? {} : { error: form.fieldErrors.email })}
        />
      </div>

      <div className={inline ? styles.consent : undefined}>
        <CheckboxField
          name="acceptedConsent"
          label={CONSENT_TEXT}
          checked={consented}
          onChange={setConsented}
          {...(form.fieldErrors.acceptedConsent === undefined
            ? {}
            : { error: form.fieldErrors.acceptedConsent })}
        />
      </div>

      <Button
        className={inline ? styles.submit : undefined}
        type="submit"
        disabled={form.status === "submitting"}
      >
        {form.status === "submitting" ? "Sending…" : "Subscribe"}
      </Button>
    </>
  );

  return (
    <form
      className={`${styles.form} ${inline ? styles.inline : ""}`.trim()}
      onSubmit={form.onSubmit}
      noValidate
    >
      {/*
        The card's pitch, as the row's first item (`WEB-035`). Only the inline variant has
        one; the standalone form carries its own `intro` below.
      */}
      {pitch === undefined ? null : <div className={styles.pitchSlot}>{pitch}</div>}

      {labelledExternally ? null : (
        <div className={styles.intro}>
          <h2 className={styles.title}>Get the board by email</h2>
          <p className={styles.lede}>
            Optional, and no account is involved. Dealbid emails about new software deals and
            nothing else.
          </p>
        </div>
      )}

      {form.status === "error" ? (
        <FormError
          title={messageForError(form.error, SUBSCRIPTION_MESSAGES)}
          requestId={form.error?.requestId}
        />
      ) : null}

      {inline ? <div className={styles.formColumn}>{controls}</div> : controls}
    </form>
  );
}
